import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Navigation } from "@/components/Navigation";
import { Link } from "react-router-dom";

const SignIn = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const navigate = useNavigate();
    const { setUser, setToken, setCurrentDietId } = useAuth();
    const { toast } = useToast();
    const { t } = useTranslation();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const response = await fetch("/api/auth/signin", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ email, password }),
            });

            const data = await response.json();

            if (response.ok) {
                const { profilePicture, ...user } = data.user;
                setUser({ ...user, picture: profilePicture });
                setToken(data.token);
                setCurrentDietId(data.currentDietId);
                toast({
                    title: t("auth.signIn.success"),
                    description: t("auth.signIn.redirecting"),
                });
                navigate("/dashboard");
            } else {
                toast({
                    variant: "destructive",
                    title: t("auth.error"),
                    description: data.message || t("auth.signIn.invalidCredentials"),
                });
            }
        } catch (error) {
            toast({
                variant: "destructive",
                title: t("auth.error"),
                description: t("auth.signIn.error"),
            });
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <Navigation />
            <div className="pt-24 flex items-center justify-center px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full space-y-8">
                    <div>
                        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">{t("auth.signIn.title")}</h2>
                    </div>
                    <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                        <div className="rounded-md shadow-sm -space-y-px">
                            <div>
                                <label htmlFor="email-address" className="sr-only">
                                    {t("auth.email")}
                                </label>
                                <input
                                    id="email-address"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                                    placeholder={t("auth.email")}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </div>
                            <div>
                                <label htmlFor="password" className="sr-only">
                                    {t("auth.password")}
                                </label>
                                <input
                                    id="password"
                                    name="password"
                                    type="password"
                                    autoComplete="current-password"
                                    required
                                    className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                                    placeholder={t("auth.password")}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                            </div>
                        </div>

                        <div>
                            <button
                                type="submit"
                                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                            >
                                {t("auth.signIn.button")}
                            </button>
                        </div>
                    </form>

                    <div className="text-center text-sm">
                        <span className="text-gray-600">{t("auth.signIn.noAccount")}</span>{" "}
                        <Link to="/signup" className="font-medium text-primary hover:text-primary/90">
                            {t("auth.signIn.signUp")}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignIn;
